import { useState } from "react";
import type { FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { api } from "../lib/api";
import { hasToken, setToken } from "../lib/auth";
import { formatPhoneNational } from "../lib/format";
import { AUTH_TRANSPORT_HEADER, logout } from "../lib/session";

type IdentifierKind = "phone" | "email";

// รหัส error จาก API แปลเป็นข้อความภาษาลาวเอง (ห้ามโชว์ message ดิบจาก server เพราะ
// เป็นภาษาอังกฤษ เช่น "invalid credentials" — ผิด ADR-0011 ที่ UI ต้องเป็นภาษาลาวล้วน)
function loginErrorMessage(code: string | undefined, status: number): string {
  switch (code) {
    case "UNAUTHORIZED":
      return "ອີເມວ/ເບີໂທ ຫຼື ລະຫັດ PIN ບໍ່ຖືກຕ້ອງ";
    case "RATE_LIMITED":
      return "ພະຍາຍາມຫຼາຍເກີນໄປ ກະລຸນາລໍຖ້າແລ້ວລອງໃໝ່";
    case "VALIDATION_ERROR":
    case "BAD_REQUEST":
      return "ຂໍ້ມູນທີ່ປ້ອນບໍ່ຖືກຕ້ອງ";
    default:
      return status === 0 ? "ຕິດຕໍ່ API ບໍ່ໄດ້" : `ເຂົ້າສູ່ລະບົບບໍ່ສຳເລັດ (HTTP ${status})`;
  }
}

export function LoginPage() {
  const navigate = useNavigate();
  // session.ts ส่งมาที่ ?expired=1 เมื่อต่ออายุไม่ได้ — ต้องบอกว่าทำไมถึงถูกพากลับมา
  const [params] = useSearchParams();
  const expired = params.get("expired") === "1";
  const [kind, setKind] = useState<IdentifierKind>("email");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (hasToken()) return <Navigate to="/" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const phoneDigits = phone.replace(/\D/g, "");
    if (kind === "phone" && phoneDigits.length < 8) {
      setError("ຮູບແບບເບີໂທບໍ່ຖືກຕ້ອງ");
      return;
    }
    if (kind === "email" && !email.trim().includes("@")) {
      setError("ຮູບແບບອີເມວບໍ່ຖືກຕ້ອງ");
      return;
    }
    if (!/^\d{6}$/.test(pin)) {
      setError("ລະຫັດ PIN ຕ້ອງເປັນຕົວເລກ 6 ຫຼັກ");
      return;
    }

    // แปลงเบอร์/อีเมลแบบหยาบพอ — API เป็นคนตัดสิน format จริง (`domain.ParseIdentifier`
    // รับได้ทั้ง +8562055551234 / 02055551234 / 2055551234 อยู่แล้ว)
    const identifier = kind === "phone" ? `+856${phoneDigits}` : email.trim().toLowerCase();

    setBusy(true);
    const { data, error: apiError, response } = await api.POST("/v1/auth/login", {
      body: { identifier, pin },
      // refresh token ไปอยู่ใน cookie httpOnly แทน JSON (lib/session.ts)
      headers: AUTH_TRANSPORT_HEADER,
    });
    setBusy(false);

    if (apiError || !data) {
      setError(loginErrorMessage(apiError?.error?.code, response.status));
      return;
    }
    if (data.user.role === "citizen") {
      // login ผ่านแล้ว = มี cookie ติดมาด้วย ต้องเพิกถอนทิ้ง ไม่ใช่ปล่อยค้างในเบราว์เซอร์
      void logout();
      setError("ບັນຊີນີ້ບໍ່ແມ່ນເຈົ້າໜ້າທີ່ — ບໍ່ມີສິດເຂົ້າລະບົບຈັດການ");
      return;
    }
    setToken(data.access_token);
    navigate("/", { replace: true });
  }

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100%",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--space-4)",
      }}
    >
      <form onSubmit={onSubmit} className="card stack" style={{ width: "100%", maxWidth: "26rem" }}>
        <div style={{ textAlign: "center" }}>
          <img
            src="/images/mof_emblem.webp"
            alt=""
            width={56}
            height={56}
            style={{ margin: "0 auto var(--space-3)", display: "block" }}
          />
          <div className="micro">SDDS ADMIN</div>
          <h1 className="section-title">ເຂົ້າສູ່ລະບົບ</h1>
          <p className="page-header__subtitle">ສຳລັບພະນັກງານຕາແສງ ແລະ ຜູ້ບໍລິຫານລະບົບ</p>
        </div>

        <div className="tab-group">
          <button
            type="button"
            className={`tab-btn${kind === "phone" ? " is-active" : ""}`}
            onClick={() => {
              setKind("phone");
              setError(null);
            }}
          >
            ເບີໂທ
          </button>
          <button
            type="button"
            className={`tab-btn${kind === "email" ? " is-active" : ""}`}
            onClick={() => {
              setKind("email");
              setError(null);
            }}
          >
            ອີເມວ
          </button>
        </div>

        {kind === "phone" ? (
          <div>
            <label htmlFor="phone" className="form-field-label">
              ເບີໂທລະສັບ<span className="required"> *</span>
            </label>
            <div className="input-phone">
              <span className="prefix num" aria-hidden="true">
                +856
              </span>
              <input
                id="phone"
                className="num"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                value={phone}
                onChange={(e) => setPhone(formatPhoneNational(e.target.value))}
                placeholder="20 XXXX XXXX"
              />
            </div>
          </div>
        ) : (
          <div>
            <label htmlFor="email" className="form-field-label">
              ອີເມວ<span className="required"> *</span>
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              className="input-field"
            />
          </div>
        )}

        <div>
          <label htmlFor="pin" className="form-field-label">
            ລະຫັດ PIN<span className="required"> *</span>
          </label>
          <input
            id="pin"
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            autoComplete="current-password"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="ຕົວເລກ 6 ຫຼັກ"
            className="input-field"
            style={{ fontFamily: "ui-monospace, monospace", letterSpacing: "0.3em" }}
          />
        </div>

        {!error && expired && <p className="field-error">ໝົດເວລາເຂົ້າລະບົບ ກະລຸນາເຂົ້າສູ່ລະບົບໃໝ່</p>}
        {error && <p className="field-error">{error}</p>}

        <button type="submit" disabled={busy} className="btn-primary btn-lg btn-block">
          {busy ? "ກຳລັງເຂົ້າສູ່ລະບົບ…" : "ເຂົ້າສູ່ລະບົບ"}
        </button>

        <p className="caption" style={{ textAlign: "center" }}>
          ບັນຊີພະນັກງານສ້າງໂດຍຜູ້ບໍລິຫານລະບົບເທົ່ານັ້ນ
        </p>

        {/* เฉพาะ `vite dev` — build จริงตัดทิ้งทั้งก้อน (บัญชีนี้มีแค่ในโหมด memory ห้ามโชว์บน production) */}
        {import.meta.env.DEV ? (
          <div
            style={{
              padding: "var(--space-2) var(--space-3)",
              borderRadius: "var(--radius-md)",
              background: "var(--yellow-100)",
              color: "var(--yellow-800)",
              font: "400 0.75rem/1rem var(--font-sans)",
            }}
          >
            <strong>ສຳລັບ dev:</strong> ເມື່ອແລ່ນ API ດ້ວຍ <code>make api-memory</code> ໃຊ້ບັນຊີ{" "}
            <code>admin@sdds.local</code> ລະຫັດ PIN <code>123456</code> ໄດ້ເລີຍ
          </div>
        ) : null}
      </form>
    </div>
  );
}
